import { useState } from 'react'
import { useUpdatePayment, useDeletePayment } from '../hooks/usePayments'
import { useInvoices } from '@/features/invoices/hooks/useInvoices'
import { Modal } from '@/components/ui/Modal'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Badge } from '@/components/ui/Badge'
import { Edit, Trash2, Save, X } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/format'
import { useForm } from 'react-hook-form'

interface PaymentDetailModalProps {
  payment: any
  isOpen: boolean
  onClose: () => void
}

export function PaymentDetailModal({ payment, isOpen, onClose }: PaymentDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')
  const updatePayment = useUpdatePayment()
  const deletePayment = useDeletePayment()
  const { data: allInvoices } = useInvoices()

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      payment_date: payment.payment_date,
      amount: payment.amount,
      payment_method: payment.payment_method,
      applied_to_baseline: payment.applied_to_baseline,
      notes: payment.notes || '',
    },
  })

  const handleUpdate = async (data: any) => {
    const invoice_ids = selectedInvoiceId ? [selectedInvoiceId] : []
    await updatePayment.mutateAsync({
      id: payment.id,
      updates: {
        ...data,
        amount: parseFloat(data.amount),
        invoice_ids,
      },
    })
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete this payment of ${formatCurrency(payment.amount)}? This action cannot be undone.`)) {
      return
    }

    await deletePayment.mutateAsync(payment.id)
    onClose()
  }

  const linkedInvoices = allInvoices?.filter(inv =>
    payment.invoice_ids?.includes(inv.id)
  ) || []

  // Get open invoices for this payment's customer
  const customerInvoices = allInvoices?.filter(inv =>
    inv.customer_id === payment.customer_id &&
    (inv.status === 'outstanding' || inv.status === 'partial_payment') &&
    inv.balance_due > 0
  ) || []

  const paymentMethodOptions = [
    { value: 'check', label: 'Check' },
    { value: 'wire', label: 'Wire Transfer' },
    { value: 'ach', label: 'ACH' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'cash', label: 'Cash' },
    { value: 'other', label: 'Other' },
  ]

  if (isEditing) {
    return (
      <Modal isOpen={isOpen} onClose={() => setIsEditing(false)} title="Edit Payment" size="lg">
        <form onSubmit={handleSubmit(handleUpdate)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Payment Date"
              type="date"
              {...register('payment_date')}
              error={errors.payment_date?.message as string}
              required
            />
            <Input
              label="Amount"
              type="number"
              step="0.01"
              {...register('amount')}
              error={errors.amount?.message as string}
              required
            />
          </div>

          <Select
            label="Payment Method"
            {...register('payment_method')}
            options={paymentMethodOptions}
            error={errors.payment_method?.message as string}
            required
          />

          {customerInvoices.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apply to Invoice (Optional)
              </label>
              <select
                value={selectedInvoiceId}
                onChange={(e) => setSelectedInvoiceId(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Select an invoice...</option>
                {customerInvoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoice_number} - {formatCurrency(inv.balance_due)} due
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {customerInvoices.length} open invoice(s) available
              </p>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="applied_to_baseline_edit"
              {...register('applied_to_baseline')}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="applied_to_baseline_edit" className="text-sm font-medium text-gray-700">
              Applied to Baseline AR
            </label>
          </div>

          <Textarea
            label="Notes"
            {...register('notes')}
            error={errors.notes?.message as string}
          />

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsEditing(false)}>
              <X className="h-4 w-4 mr-2" />Cancel
            </Button>
            <Button type="submit" loading={updatePayment.isPending}>
              <Save className="h-4 w-4 mr-2" />Save Changes
            </Button>
          </div>
        </form>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Payment Details" size="lg">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{formatCurrency(payment.amount)}</h2>
            <div className="flex items-center space-x-3 mt-2">
              <Badge>{payment.payment_method}</Badge>
              {payment.applied_to_baseline && <Badge variant="info">Baseline AR</Badge>}
            </div>
          </div>
          <div className="flex space-x-2">
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4 mr-2" />Edit
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deletePayment.isPending}>
              <Trash2 className="h-4 w-4 mr-2" />Delete
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Payment Date</dt>
                <dd className="text-sm text-gray-900">{formatDate(payment.payment_date)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Amount</dt>
                <dd className="text-sm font-semibold text-gray-900">{formatCurrency(payment.amount)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                <dd className="text-sm text-gray-900">{payment.payment_method}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Type</dt>
                <dd className="text-sm text-gray-900">
                  {payment.applied_to_baseline ? 'Baseline AR' : 'New Past-Due'}
                </dd>
              </div>
              {payment.collection_fee_owed > 0 && (
                <div className="flex justify-between pt-3 border-t">
                  <dt className="text-sm font-medium text-gray-500">Collection Fee</dt>
                  <dd className="text-sm font-semibold text-gray-900">{formatCurrency(payment.collection_fee_owed)}</dd>
                </div>
              )}
            </dl>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">Customer & Client</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Client</dt>
                <dd className="text-sm text-gray-900">{payment.client?.name || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Customer</dt>
                <dd className="text-sm text-gray-900">{payment.customer?.name || '—'}</dd>
              </div>
            </dl>
          </Card>
        </div>

        {linkedInvoices.length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold mb-4">Applied to Invoices ({linkedInvoices.length})</h3>
            <div className="space-y-2">
              {linkedInvoices.map((invoice: any) => (
                <div key={invoice.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{invoice.invoice_number}</span>
                    <span className="text-sm text-gray-500 ml-3">Due: {formatDate(invoice.due_date)}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(invoice.balance_due)} remaining
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {payment.notes && (
          <Card>
            <h3 className="text-lg font-semibold mb-4">Notes</h3>
            <p className="text-sm text-gray-700">{payment.notes}</p>
          </Card>
        )}

        <div className="flex justify-end pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  )
}
